import { useSignedInIdentity, useIdentity } from "../../../../shared/useIdentity";
import React, { useEffect, useState } from "react";
import { LANGUAGE_NAMES } from "../../../../shared/languages";
import { APP_DATA } from "../../data/mockData";
import { signOut } from "../../services/authSession";
import type {
  Accommodation,
  Candidate,
  EvidenceRecord,
  Incident,
  ModalState,
  Preferences,
  Session,
} from "../../types/domain";
import { downloadText } from "../../utils/browser";
import { requestClassHandoff } from "../../../../shared/useParticipation";
import {
  acceptProctorHandover,
  initiateProctorHandover,
  listHandoverSessions,
  loadHandoverWorkspace,
  type HandoverSession,
  type HandoverTeamMember,
  type ProctorHandover,
} from "../../services/handovers";
import {
  applyProctorSessionTimerControl,
  listProctorCandidates,
  listProctorSessions,
  lockProctorSessionAdmissions,
  type ProctorCandidate,
  type ProctorSession,
} from "../../services/readiness";
import {
  recordFactualIncident,
  type FactualIncidentCategory,
  type FactualIncidentSeverity,
} from "../../services/incidents";
import {
  Button,
  EmptyState,
  Icon,
  PlatformChip,
  Progress,
  Status,
  Toggle,
  type AppCallbacks,
} from "./WorkspaceLayouts";

export function AppModal({
  modal,
  close,
  cb,
  prefs,
  setPrefs,
}: {
  modal: ModalState;
  close: () => void;
  cb: AppCallbacks;
  prefs: Preferences;
  setPrefs: React.Dispatch<React.SetStateAction<Preferences>>;
}) {
  const identity = useSignedInIdentity();
  const account = useIdentity();
  const [selectedNotification, setSelectedNotification] = useState<any | null>(null);
  const [handoffBusy, setHandoffBusy] = useState(false);
  const [handoverSessions, setHandoverSessions] = useState<HandoverSession[]>([]);
  const [handoverSessionId, setHandoverSessionId] = useState("");
  const [handoverTeam, setHandoverTeam] = useState<HandoverTeamMember[]>([]);
  const [handoverRecords, setHandoverRecords] = useState<ProctorHandover[]>([]);
  const [handoverLoading, setHandoverLoading] = useState(false);
  const [handoverSaving, setHandoverSaving] = useState(false);
  const [handoverError, setHandoverError] = useState("");
  const [roomControlSaving, setRoomControlSaving] = useState(false);
  const [roomControlError, setRoomControlError] = useState("");
  const [incidentSessions, setIncidentSessions] = useState<ProctorSession[]>([]);
  const [incidentSessionId, setIncidentSessionId] = useState("");
  const [incidentCandidates, setIncidentCandidates] = useState<ProctorCandidate[]>([]);
  const [incidentCandidateId, setIncidentCandidateId] = useState("");
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [incidentLoadFailed, setIncidentLoadFailed] = useState(false);
  const [incidentSaving, setIncidentSaving] = useState(false);
  const [incidentError, setIncidentError] = useState("");
  useEffect(() => {
    if (modal.kind !== "notifications") setSelectedNotification(null);
    if (modal.kind !== "secure-note") setRoomControlError("");
  }, [modal.kind]);
  useEffect(() => {
    if (modal.kind !== "handover") return;
    const controller = new AbortController();
    setHandoverLoading(true);
    setHandoverError("");
    void listHandoverSessions(controller.signal)
      .then(items => {
        setHandoverSessions(items);
        setHandoverSessionId(current =>
          items.some(item => item.exam_session_id === current) ? current : items[0]?.exam_session_id || "",
        );
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setHandoverError(cause instanceof Error ? cause.message : "Assigned sessions are unavailable.");
      })
      .finally(() => setHandoverLoading(false));
    return () => controller.abort();
  }, [modal.kind]);
  useEffect(() => {
    if (modal.kind !== "handover" || !handoverSessionId) return;
    const controller = new AbortController();
    setHandoverLoading(true);
    setHandoverError("");
    void loadHandoverWorkspace(handoverSessionId, controller.signal)
      .then(workspace => {
        setHandoverTeam(workspace.team);
        setHandoverRecords(workspace.handovers);
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setHandoverError(cause instanceof Error ? cause.message : "Handover workspace is unavailable.");
      })
      .finally(() => setHandoverLoading(false));
    return () => controller.abort();
  }, [modal.kind, handoverSessionId]);
  useEffect(() => {
    if (modal.kind !== "record-incident") return;
    const controller = new AbortController();
    setIncidentLoading(true);
    setIncidentLoadFailed(false);
    setIncidentError("");
    setIncidentSessions([]);
    setIncidentSessionId("");
    setIncidentCandidates([]);
    setIncidentCandidateId("");
    void listProctorSessions(controller.signal)
      .then(items => {
        setIncidentSessions(items);
        setIncidentSessionId(items[0]?.exam_session_id || "");
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setIncidentLoadFailed(true);
        setIncidentError(cause instanceof Error ? cause.message : "Assigned sessions are unavailable.");
      })
      .finally(() => setIncidentLoading(false));
    return () => controller.abort();
  }, [modal.kind]);
  useEffect(() => {
    if (modal.kind !== "record-incident" || !incidentSessionId) return;
    const controller = new AbortController();
    setIncidentLoading(true);
    setIncidentLoadFailed(false);
    setIncidentError("");
    void listProctorCandidates(incidentSessionId, controller.signal)
      .then(items => {
        setIncidentCandidates(items);
        const candidateNumber = (modal.payload as Candidate | undefined)?.candidateNo;
        setIncidentCandidateId(
          items.find(item => item.candidate_number === candidateNumber)?.candidate_user_id || "",
        );
      })
      .catch(cause => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setIncidentLoadFailed(true);
        setIncidentCandidates([]);
        setIncidentCandidateId("");
        setIncidentError(cause instanceof Error ? cause.message : "Session candidates are unavailable.");
      })
      .finally(() => setIncidentLoading(false));
    return () => controller.abort();
  }, [modal.kind, modal.payload, incidentSessionId]);
  if (!modal.kind) return null;
  const payload: any = modal.payload || {};
  const candidate: Candidate | undefined =
    modal.kind === "candidate-detail" ? payload : payload?.candidateNo ? payload : undefined;
  const session: Session | undefined = modal.kind === "session-detail" ? payload : payload?.session;
  const incident: Incident | undefined = modal.kind === "incident-detail" ? payload : payload?.incident;
  const evidence: EvidenceRecord | undefined = modal.kind === "evidence-detail" ? payload : undefined;
  const accommodation: Accommodation | undefined =
    modal.kind === "accommodation-detail" ? payload : undefined;
  const titleMap: Record<string, string> = {
    "candidate-detail": "Candidate session view",
    "session-detail": "Assessment session",
    "incident-detail": "Incident record",
    "evidence-detail": "Evidence record",
    "record-incident": "Record factual incident",
    broadcast: "Message all candidates",
    "candidate-message": "Secure message",
    calendar: "Proctor calendar",
    "proctor-team": "Assigned proctor team",
    "readiness-checklist": "Candidate preflight",
    "scope-boundary": "Role and data visibility",
    export: "Prepare report export",
    "class-handoff": "Continue to Chatoner Class",
    "lab-handoff": "Continue to Chatoner AI Labs",
    handover: "Structured proctor handover",
    emergency: "Emergency and continuity action",
    language: "Language and translation",
    notifications: "Notifications",
    profile: "Exam Proctor profile",
    "settings-help": "Proctor help",
    "accommodation-detail": "Approved accommodation",
    "break-action": "Approved break or timing action",
    "evidence-export": "Evidence export manifest",
    "policy-confirmation": "Policy acknowledgement",
    "secure-note": "Record operational note",
  };
  const subtitleMap: Record<string, string> = {
    "candidate-detail": "Session-scoped information only",
    "session-detail": "Role, roster, rules, timing and connected platform",
    "incident-detail": "Observable facts, action, evidence and human review",
    "evidence-detail": "Source, integrity, custody, access and retention",
    "record-incident": "Do not declare misconduct or an academic outcome",
    broadcast: "Procedural or support information only",
    "candidate-message": "Purpose-limited and attributable communication",
    calendar: "Assigned sessions, preflights, breaks and handovers",
    "proctor-team": "Lead, room, relief and specialist contacts",
    "readiness-checklist": "Human verification and approved alternatives",
    "scope-boundary": "Monitor, support, document and escalate",
    export: "Purpose, recipient, scope and privacy checks required",
    "class-handoff": "Independent platform · class.chatoner.com",
    "lab-handoff": "Independent platform · labs.chatoner.com",
    handover: "Transfer factual room context without speculation",
    emergency: "Safety, accessibility, safeguarding and service continuity",
    language: "Original content remains available",
    notifications: "Assigned sessions and operational actions",
    profile: "Vetting, certification, authority and availability",
    "settings-help": "Guidance for this proctor workflow",
    "accommodation-detail": "Minimum instruction necessary for fair delivery",
    "break-action": "Use only a pre-approved or newly authorized action",
    "evidence-export": "Every export is permission-checked and manifested",
    "policy-confirmation": "Human authority and privacy obligations",
    "secure-note": "Attach purpose, source and next action",
  };
  const copy = (value: string, label: string) => {
    navigator.clipboard?.writeText(value).catch(() => {});
    cb.toast(`${label} copied`, value, "good");
  };
  const wide = [
    "candidate-detail",
    "session-detail",
    "record-incident",
    "calendar",
    "scope-boundary",
    "export",
    "class-handoff",
    "lab-handoff",
    "handover",
    "emergency",
    "notifications",
    "proctor-team",
    "readiness-checklist",
    "evidence-export",
  ].includes(modal.kind || "");
  let body: React.ReactNode;
  let footer: React.ReactNode = <Button onClick={close}>Close</Button>;

  if (modal.kind === "candidate-detail" && candidate) {
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item">
            <small>Candidate</small>
            <b>
              {candidate.name}
              <br />
              {candidate.candidateNo}
            </b>
          </div>
          <div className="detail-item">
            <small>Current session state</small>
            <b>
              <Status>{candidate.status}</Status>
            </b>
          </div>
          <div className="detail-item">
            <small>Identity</small>
            <b>{candidate.identity}</b>
          </div>
          <div className="detail-item">
            <small>Device & network</small>
            <b>
              {candidate.network} · camera {candidate.camera ? "ready" : "check"} · microphone{" "}
              {candidate.microphone ? "ready" : "check"}
            </b>
          </div>
          <div className="detail-item">
            <small>Approved accommodation</small>
            <b>{candidate.accommodation}</b>
          </div>
          <div className="detail-item">
            <small>Language</small>
            <b>{candidate.language}</b>
          </div>
          <div className="detail-item">
            <small>Time remaining</small>
            <b>{candidate.timeRemaining}</b>
          </div>
          <div className="detail-item">
            <small>Last session event</small>
            <b>{candidate.lastEvent}</b>
          </div>
          <div className="detail-item full">
            <small>Proctor boundary</small>
            <b>
              Provide procedural, technical, accessibility and wellbeing support. Do not interpret assessment
              content, grade, accuse or expose another candidate.
            </b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="shield" size={16} />
          Automated or system signals may request human attention, but they are not proof and must not be
          presented as an outcome.
        </div>
      </>
    );
    footer = (
      <>
        <Button icon="message" onClick={() => cb.openModal("candidate-message", candidate)}>
          Secure message
        </Button>
        <Button icon="history" onClick={() => cb.openModal("break-action", candidate)}>
          Approved break
        </Button>
        <Button icon="plus" variant="orange" onClick={() => cb.openModal("record-incident", candidate)}>
          Record incident
        </Button>
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "session-detail" && session) {
    const readiness = Math.round((session.ready / session.candidates) * 100);
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item full">
            <small>Assessment</small>
            <b>
              {session.title}
              <br />
              {session.program}
            </b>
          </div>
          <div className="detail-item">
            <small>Platform</small>
            <b>
              <PlatformChip platform={session.platform} />
            </b>
          </div>
          <div className="detail-item">
            <small>Status</small>
            <b>
              <Status>{session.status}</Status>
            </b>
          </div>
          <div className="detail-item">
            <small>Date and time</small>
            <b>
              {session.date}
              <br />
              {session.time}
            </b>
          </div>
          <div className="detail-item">
            <small>Session code</small>
            <b>{session.code}</b>
          </div>
          <div className="detail-item">
            <small>Candidate readiness</small>
            <b>
              {session.ready} of {session.candidates} · {readiness}%
            </b>
            <Progress value={readiness} green />
          </div>
          <div className="detail-item">
            <small>Assigned role</small>
            <b>
              {session.role}
              <br />
              Lead: {session.lead}
            </b>
          </div>
          <div className="detail-item">
            <small>Risk & evidence mode</small>
            <b>
              {session.risk}
              <br />
              {session.recording || "Published policy"}
            </b>
          </div>
          <div className="detail-item full">
            <small>Location and scope</small>
            <b>{session.location}</b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          The proctor receives only the roster, rules, readiness, accommodation instructions and support
          contacts necessary for this assigned session.
        </div>
      </>
    );
    footer = (
      <>
        <Button icon="copy" onClick={() => copy(session.code, "Session code")}>
          Copy code
        </Button>
        {session.platform.includes("Class") && (
          <Button icon="external" variant="primary" onClick={() => cb.openClass(session, "proctor")}>
            Open Class
          </Button>
        )}
        {session.platform.includes("Labs") && (
          <Button icon="external" variant="primary" onClick={() => cb.openLab(session, "proctor-home")}>
            Open Labs
          </Button>
        )}
        {session.platform.includes("Academy") && (
          <Button
            icon="monitor"
            variant="green"
            onClick={() => {
              close();
              cb.navigate("rooms", "active-exam-room");
            }}
          >
            Open room
          </Button>
        )}
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "incident-detail" && incident) {
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item">
            <small>Incident</small>
            <b>{incident.id}</b>
          </div>
          <div className="detail-item">
            <small>Status</small>
            <b>
              <Status>{incident.status}</Status>
            </b>
          </div>
          <div className="detail-item">
            <small>Candidate</small>
            <b>{incident.candidate}</b>
          </div>
          <div className="detail-item">
            <small>Category & severity</small>
            <b>
              {incident.category} · {incident.severity}
            </b>
          </div>
          <div className="detail-item">
            <small>Time and source</small>
            <b>
              {incident.time}
              <br />
              {incident.source}
            </b>
          </div>
          <div className="detail-item">
            <small>Owner</small>
            <b>{incident.owner}</b>
          </div>
          <div className="detail-item full">
            <small>Summary</small>
            <b>{incident.summary}</b>
          </div>
          <div className="detail-item full">
            <small>Observable facts</small>
            <b>{incident.facts}</b>
          </div>
          <div className="detail-item full">
            <small>Action already taken</small>
            <b>{incident.action}</b>
          </div>
          <div className="detail-item full">
            <small>Linked evidence</small>
            <b>{incident.evidence.join(", ") || "No evidence reference"}</b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="alert" size={16} />
          This is a preview operational record. Statement requests, escalation and closure are unavailable
          until governed server records and reviewer routing are connected.
        </div>
      </>
    );
    footer = (
      <>
        <Button icon="message" disabled>
          Request statement
        </Button>
        <Button icon="arrow" variant="primary" disabled>
          Escalate
        </Button>
        <Button icon="check" variant="green" disabled>
          Resolve
        </Button>
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "evidence-detail" && evidence) {
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item">
            <small>Evidence ID</small>
            <b>{evidence.id}</b>
          </div>
          <div className="detail-item">
            <small>Status</small>
            <b>
              <Status>{evidence.status}</Status>
            </b>
          </div>
          <div className="detail-item">
            <small>Type</small>
            <b>{evidence.type}</b>
          </div>
          <div className="detail-item">
            <small>Candidate</small>
            <b>{evidence.candidate}</b>
          </div>
          <div className="detail-item">
            <small>Captured</small>
            <b>{evidence.captured}</b>
          </div>
          <div className="detail-item">
            <small>Source</small>
            <b>{evidence.source}</b>
          </div>
          <div className="detail-item">
            <small>Custodian</small>
            <b>{evidence.custodian}</b>
          </div>
          <div className="detail-item">
            <small>Access</small>
            <b>{evidence.access}</b>
          </div>
          <div className="detail-item">
            <small>Retention</small>
            <b>{evidence.retention}</b>
          </div>
          <div className="detail-item">
            <small>Integrity hash</small>
            <b>{evidence.hash}</b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="database" size={16} />
          Opening, copying or exporting this record must remain attributable and within the stated purpose.
        </div>
      </>
    );
    footer = (
      <>
        <Button icon="copy" onClick={() => copy(evidence.hash, "Evidence hash")}>
          Copy hash
        </Button>
        <Button icon="download" variant="primary" onClick={() => cb.openModal("evidence-export", evidence)}>
          Prepare export
        </Button>
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "record-incident") {
    body = (
      <form
        id="incident-form"
        onSubmit={async event => {
          event.preventDefault();
          if (!incidentSessionId || incidentLoading || incidentSaving) return;
          const data = new FormData(event.currentTarget);
          const summary = String(data.get("summary") || "").trim();
          const facts = String(data.get("facts") || "").trim();
          const action = String(data.get("action") || "").trim();
          setIncidentSaving(true);
          setIncidentError("");
          try {
            const receipt = await recordFactualIncident(incidentSessionId, {
              ...(incidentCandidateId ? { candidateUserId: incidentCandidateId } : {}),
              category: String(data.get("category")) as FactualIncidentCategory,
              severity: String(data.get("severity")) as FactualIncidentSeverity,
              observableFacts: `${summary}\n\n${facts}`,
              source: "human_observation",
              ...(action ? { immediateAction: action } : {}),
            });
            cb.toast(
              "Factual incident recorded",
              `Reference ${receipt.id}. No finding or academic outcome was created.`,
              "good",
            );
            window.dispatchEvent(
              new CustomEvent("academy:proctor-incident-recorded", {
                detail: { examSessionId: incidentSessionId },
              }),
            );
            close();
          } catch (cause) {
            setIncidentError(
              cause instanceof Error ? cause.message : "The factual incident could not be recorded.",
            );
          } finally {
            setIncidentSaving(false);
          }
        }}
      >
        <div className="form-grid">
          <label className="field">
            <span>Assigned session</span>
            <select
              aria-label="Assigned session"
              className="select"
              value={incidentSessionId}
              onChange={event => setIncidentSessionId(event.target.value)}
              disabled={incidentLoading}
              required
            >
              {incidentSessions.length === 0 && <option value="">No assigned session available</option>}
              {incidentSessions.map(item => (
                <option key={item.exam_session_id} value={item.exam_session_id}>
                  {item.assessment_title} · {item.cohort_name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Candidate</span>
            <select
              aria-label="Candidate"
              name="candidate"
              className="select"
              value={incidentCandidateId}
              onChange={event => setIncidentCandidateId(event.target.value)}
              disabled={incidentLoading}
            >
              <option value="">Room-wide / no candidate</option>
              {incidentCandidates.map(item => (
                <option key={item.candidate_user_id} value={item.candidate_user_id}>
                  {item.full_name} · {item.candidate_number}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Category</span>
            <select name="category" className="select" defaultValue="technical">
              <option value="technical">Technical</option>
              <option value="accessibility">Accessibility</option>
              <option value="wellbeing">Wellbeing</option>
              <option value="integrity_observation">Integrity observation</option>
              <option value="safeguarding">Safeguarding</option>
              <option value="other">Other operational event</option>
            </select>
          </label>
          <label className="field">
            <span>Severity for triage</span>
            <select name="severity" className="select" defaultValue="normal">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </label>
          <label className="field full">
            <span>Source</span>
            <input className="input" value="Human observation" readOnly />
          </label>
          <label className="field full">
            <span>Factual summary</span>
            <input
              name="summary"
              className="input"
              required
              maxLength={200}
              placeholder="One neutral sentence describing the observable event"
            />
          </label>
          <label className="field full">
            <span>Observable facts</span>
            <textarea
              name="facts"
              className="textarea"
              required
              minLength={5}
              maxLength={1790}
              placeholder="What happened, when, for how long, and from which source? Do not infer intent or outcome."
            />
          </label>
          <label className="field full">
            <span>Support or action taken</span>
            <textarea
              name="action"
              className="textarea"
              maxLength={1000}
              placeholder="Procedure explained, technical support called, timer paused under policy, no action yet…"
            />
          </label>
        </div>
        <div className="form-note">
          <Icon name="shield" size={16} />
          {incidentError ||
            (incidentLoading
              ? "Loading assigned sessions and verified candidate sittings…"
              : "Only observable facts are saved. This record is not a misconduct finding or academic outcome.")}
        </div>
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button
          type="submit"
          form="incident-form"
          className="btn btn-orange"
          disabled={!incidentSessionId || incidentLoading || incidentLoadFailed || incidentSaving}
        >
          <Icon name="plus" size={16} />
          <span>{incidentSaving ? "Recording…" : "Save factual incident"}</span>
        </button>
      </>
    );
  } else if (modal.kind === "broadcast") {
    body = (
      <form id="broadcast-form" onSubmit={e => e.preventDefault()}>
        <div className="form-grid">
          <label className="field">
            <span>Audience</span>
            <select name="audience" className="select">
              <option>All active candidates</option>
              <option>Candidates waiting for admission</option>
              <option>Candidates reconnecting</option>
              <option>Approved break candidates</option>
              <option>Proctor team only</option>
            </select>
          </label>
          <label className="field">
            <span>Language delivery</span>
            <select name="language" className="select">
              <option>Primary + approved translations</option>
              <option>Primary language only</option>
              {LANGUAGE_NAMES.map(language => (
                <option key={language}>{language}</option>
              ))}
            </select>
          </label>
          <label className="field full">
            <span>Procedural message</span>
            <textarea
              className="textarea"
              name="message"
              required
              defaultValue="The assessment remains active. Use the Help control for technical or accessibility support. Do not share assessment content in chat."
            />
          </label>
          <label className="field full">
            <span>Reason for broadcast</span>
            <input className="input" name="reason" required defaultValue="Session procedure reminder" />
          </label>
        </div>
        <div className="form-note">
          <Icon name="message" size={16} />
          Broadcast delivery is unavailable until verified recipients and server delivery are connected. No
          message is sent.
        </div>
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button type="submit" form="broadcast-form" className="btn btn-primary" disabled>
          <Icon name="send" size={16} />
          <span>Send broadcast</span>
        </button>
      </>
    );
  } else if (modal.kind === "candidate-message") {
    const recipient = (payload as Candidate)?.name || payload?.name || "Assigned support channel";
    body = (
      <form id="message-form" onSubmit={e => e.preventDefault()}>
        <div className="detail-item">
          <small>Recipient</small>
          <b>{recipient}</b>
        </div>
        <div className="form-grid" style={{ marginTop: 10 }}>
          <label className="field full">
            <span>Purpose</span>
            <select name="purpose" className="select">
              <option>Procedural support</option>
              <option>Technical support</option>
              <option>Accessibility support</option>
              <option>Candidate statement request</option>
              <option>Proctor-team handover</option>
              <option>Safeguarding escalation</option>
            </select>
          </label>
          <label className="field full">
            <span>Message</span>
            <textarea
              className="textarea"
              name="message"
              required
              placeholder="Write a neutral, purpose-limited message…"
            />
          </label>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          Secure messaging is unavailable until the recipient and delivery record are verified. No message is
          sent.
        </div>
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button type="submit" form="message-form" className="btn btn-primary" disabled>
          <Icon name="send" size={16} />
          <span>Send securely</span>
        </button>
      </>
    );
  } else if (modal.kind === "calendar") {
    body = (
      <div className="session-card-grid">
        {APP_DATA.sessions.map((s: Session) => (
          <article className="session-card" key={s.id}>
            <div className="session-card-top">
              <PlatformChip platform={s.platform} />
              <Status>{s.status}</Status>
            </div>
            <h3>{s.title}</h3>
            <p>
              {s.date} · {s.time}
            </p>
            <div className="session-meta-grid">
              <span>
                <small>Code</small>
                <b>{s.code}</b>
              </span>
              <span>
                <small>Role</small>
                <b>{s.role}</b>
              </span>
            </div>
            <Button small icon="eye" onClick={() => cb.openModal("session-detail", s)}>
              Details
            </Button>
          </article>
        ))}
      </div>
    );
    footer = (
      <>
        <Button
          icon="download"
          onClick={() => {
            const ics = `BEGIN:VCALENDAR\nVERSION:2.0\n${APP_DATA.sessions.map((s: Session) => `BEGIN:VEVENT\nSUMMARY:${s.title}\nDESCRIPTION:${s.platform} · ${s.code}\nEND:VEVENT`).join("\n")}\nEND:VCALENDAR`;
            downloadText("Chatoner-Proctor-Schedule.ics", ics, "text/calendar");
            cb.toast("Calendar downloaded", "The assigned proctor schedule was prepared.", "good");
          }}
        >
          Download calendar
        </Button>
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "proctor-team") {
    const people = [
      ["Amina Mutesi", "Lead Exam Proctor", "Live room authority", "AM"],
      ["Peter Ouma", "Technical Proctor", "Connectivity and submission continuity", "PO"],
      ["Grace Namusoke", "Integrity Reviewer", "Receives escalations; decides review outcome", "GN"],
      ["Lydia Atim", "Accessibility Specialist", "Approved accommodations and barrier recovery", "LA"],
      ["Joel Ssempala", "Relief Proctor", "Scheduled handover at 15:00 EAT", "JS"],
      ["Safeguarding Team", "Protected response", "Restricted wellbeing and safety channel", "ST"],
    ];
    body = (
      <div className="list">
        {people.map(([name, role, scope, initial]) => (
          <div className="list-row" key={name}>
            <span className="avatar">{initial}</span>
            <span className="list-copy">
              <b>
                {name} · {role}
              </b>
              <span>{scope}</span>
            </span>
            <Button small icon="message" onClick={() => cb.openModal("candidate-message", { name })}>
              Message
            </Button>
          </div>
        ))}
      </div>
    );
  } else if (modal.kind === "readiness-checklist") {
    const checks = [
      [
        "Human identity verification",
        "Manual document or approved alternative verification; no autonomous biometric decision.",
        "112/124 verified",
      ],
      ["Device and network", "Browser, camera, microphone, bandwidth, power and fallback.", "116/124 ready"],
      ["Room and environment", "Proportionate published check; no broader surveillance.", "109/124 complete"],
      [
        "Allowed materials",
        "Exam-specific references, calculators, tools and accessibility aids.",
        "118/124 confirmed",
      ],
      ["Approved accommodations", "Only delivery instructions needed by the proctor.", "12 active plans"],
      [
        "Consent and rules",
        "Candidate received the session policy, evidence scope and support path.",
        "120/124 complete",
      ],
    ];
    body = (
      <div className="checklist">
        {checks.map(([title, text, status], i) => (
          <div className="check-row" key={title}>
            <span>
              <Icon name={i < 4 ? "check" : "clipboard"} />
            </span>
            <span className="check-row-copy">
              <b>{title}</b>
              <small>{text}</small>
            </span>
            <Status>{status}</Status>
          </div>
        ))}
      </div>
    );
    footer = (
      <>
        <Button
          icon="download"
          onClick={() =>
            downloadText(
              "candidate-preflight.md",
              checks.map(v => `- ${v[0]}: ${v[2]}\n  ${v[1]}`).join("\n"),
            )
          }
        >
          Download checklist
        </Button>
        <Button
          variant="primary"
          icon="check"
          onClick={() => {
            cb.toast(
              "Preflight refreshed",
              "Readiness records were checked against the assigned session.",
              "good",
            );
            close();
          }}
        >
          Refresh checks
        </Button>
      </>
    );
  } else if (modal.kind === "scope-boundary") {
    body = (
      <div className="boundary-grid">
        <div className="boundary-card allow">
          <h3>
            <Icon name="check" />
            Visible to assigned proctor
          </h3>
          <ul>
            {[
              "Candidate name and session identifier.",
              "Current admission or room state.",
              "Manual identity-verification result.",
              "Device and connection readiness.",
              "Minimum approved accommodation instruction.",
              "Procedural and support messages.",
              "Session-scoped incident and evidence references.",
            ].map(v => (
              <li key={v}>
                <Icon name="check" size={13} />
                {v}
              </li>
            ))}
          </ul>
        </div>
        <div className="boundary-card deny">
          <h3>
            <Icon name="lock" />
            Not automatically visible
          </h3>
          <ul>
            {[
              "Grades, draft marking or assessor deliberation.",
              "Medical diagnosis or protected support records.",
              "Unrelated course, Class or Labs histories.",
              "Private learner prompts, files or working outputs.",
              "Another group or session’s private messages.",
              "Automated suspicion score or facial-recognition decision.",
              "Credential recommendation or sanctions.",
            ].map(v => (
              <li key={v}>
                <Icon name="close" size={13} />
                {v}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  } else if (modal.kind === "export" || modal.kind === "evidence-export") {
    const evidenceMode = modal.kind === "evidence-export";
    body = (
      <form id="export-form" onSubmit={e => e.preventDefault()}>
        <div className="form-grid">
          <label className="field full">
            <span>Export title</span>
            <input
              name="title"
              className="input"
              defaultValue={
                payload?.title || (evidenceMode ? "Evidence export manifest" : "Session operations report")
              }
            />
          </label>
          <label className="field">
            <span>Scope</span>
            <select name="scope" className="select">
              <option>Assigned sessions only</option>
              <option>Current session only</option>
              <option>Selected incident records</option>
              <option>Selected evidence records</option>
              <option>Operational aggregate only</option>
            </select>
          </label>
          <label className="field">
            <span>Recipient</span>
            <select name="recipient" className="select">
              <option>Assessment Operations</option>
              <option>Authorized Integrity Reviewer</option>
              <option>Accessibility Office</option>
              <option>Technical Operations</option>
              <option>Institution Assessment Board</option>
            </select>
          </label>
          <label className="field full">
            <span>Purpose</span>
            <input
              name="purpose"
              className="input"
              required
              defaultValue="Authorized operational review and quality assurance"
            />
          </label>
          <label className="field">
            <span>Format</span>
            <select name="format" className="select">
              <option>Markdown manifest</option>
              <option>CSV package</option>
              <option>JSON evidence manifest</option>
              <option>PDF request (production service)</option>
            </select>
          </label>
          <label className="field">
            <span>Retention after export</span>
            <select name="retention" className="select">
              <option>Use source record retention</option>
              <option>30 days</option>
              <option>90 days</option>
              <option>Legal hold (approval required)</option>
            </select>
          </label>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          Export is unavailable until Academy verifies the requester, purpose, recipient, field scope and
          retention. No package is generated.
        </div>
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button type="submit" form="export-form" className="btn btn-primary" disabled>
          <Icon name="download" size={16} />
          <span>Prepare export</span>
        </button>
      </>
    );
  } else if (modal.kind === "class-handoff" || modal.kind === "lab-handoff") {
    const pSession: Session = payload.session || APP_DATA.sessions[0];
    const isClass = modal.kind === "class-handoff";
    const isCanonicalClassSession = isClass && cb.classSessions.some(session => session.id === pSession.id);
    body = (
      <>
        <div className="platform-hero" style={{ marginBottom: 10 }}>
          <div className="platform-hero-top">
            <span className="platform-logo">
              <Icon name={isClass ? "video" : "flask"} size={24} />
            </span>
            <div>
              <h3>{isClass ? "Chatoner Class" : "Chatoner AI Labs"}</h3>
              <p>
                {isClass
                  ? "Live assessment hosting, media, admission, captions and recordings remain owned by Class."
                  : "Practical runtimes, prompts, files, compute, safety and Lab-native evidence remain owned by Labs."}
              </p>
            </div>
          </div>
          <div className="connection-line">
            <i />
            {isClass
              ? "One-time exam handoff requires the current assigned session"
              : "Exam connection unavailable pending verified assignment and destination handoff"}
          </div>
        </div>
        <div className="detail-grid">
          <div className="detail-item">
            <small>Assessment</small>
            <b>{pSession.title}</b>
          </div>
          <div className="detail-item">
            <small>Preview session code</small>
            <b>{pSession.code}</b>
          </div>
          <div className="detail-item">
            <small>Role</small>
            <b>Exam Proctor preview</b>
          </div>
          <div className="detail-item">
            <small>Destination</small>
            <b>{payload.destination || payload.mode || "proctor"}</b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="shield" size={16} />
          {isClass
            ? "Academy will verify this exact exam session and current Proctor assignment before issuing a one-time destination."
            : "A launch link is unavailable until Academy verifies the exact exam session, current Proctor assignment and a one-time destination capability. No data was sent."}
        </div>
      </>
    );
    footer = (
      <>
        <Button onClick={close}>Close</Button>
        {isCanonicalClassSession && (
          <button
            type="button"
            className="btn btn-primary"
            disabled={handoffBusy}
            onClick={() => {
              setHandoffBusy(true);
              void requestClassHandoff(
                "exam_session",
                pSession.id,
                payload.mode === "rehearsal" ? "rehearsal" : "proctor",
              )
                .then(handoff => window.location.assign(handoff.destinationUrl))
                .catch(cause =>
                  cb.toast(
                    "Class handoff unavailable",
                    cause instanceof Error ? cause.message : "Refresh and try again.",
                    "warn",
                  ),
                )
                .finally(() => setHandoffBusy(false));
            }}
          >
            <Icon name="external" size={16} />
            <span>{handoffBusy ? "Verifying…" : "Continue to Class"}</span>
          </button>
        )}
      </>
    );
  } else if (modal.kind === "handover") {
    const selectedSession = handoverSessions.find(item => item.exam_session_id === handoverSessionId);
    const targetTeam = handoverTeam.filter(item => item.assignment_id !== selectedSession?.assignment_id);
    body = (
      <form
        id="handover-form"
        onSubmit={event => {
          event.preventDefault();
          if (!selectedSession) return;
          const values = new FormData(event.currentTarget);
          const target = targetTeam.find(item => item.assignment_id === values.get("to"));
          if (!target) return;
          setHandoverSaving(true);
          setHandoverError("");
          void initiateProctorHandover(selectedSession.exam_session_id, {
            targetAssignmentId: target.assignment_id,
            expectedSourceAssignmentVersion: selectedSession.assignment_version,
            expectedTargetAssignmentVersion: target.assignment_version,
            roomState: String(values.get("state")) as ProctorHandover["context"]["roomState"],
            openCandidateSupport: String(values.get("support") || "").trim(),
            openIncidentReferences: String(values.get("incidents") || "").trim(),
            requiredNextAction: String(values.get("next") || "").trim(),
          })
            .then(() => loadHandoverWorkspace(selectedSession.exam_session_id))
            .then(workspace => {
              setHandoverTeam(workspace.team);
              setHandoverRecords(workspace.handovers);
              cb.toast(
                "Handover awaiting acceptance",
                "The factual context is durable; assignment and academic authority are unchanged.",
                "good",
              );
            })
            .catch(cause => {
              const message = cause instanceof Error ? cause.message : "Refresh and try again.";
              setHandoverError(message);
              cb.toast("Handover not recorded", message, "warn");
            })
            .finally(() => setHandoverSaving(false));
        }}
      >
        <div className="form-grid">
          <label className="field full">
            <span>Assigned exam session</span>
            <select
              name="session"
              className="select"
              value={handoverSessionId}
              onChange={event => setHandoverSessionId(event.target.value)}
              disabled={handoverLoading || handoverSaving}
            >
              {handoverSessions.map(item => (
                <option key={item.exam_session_id} value={item.exam_session_id}>
                  {item.assessment_title} · {item.cohort_name} · {item.assignment_role}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Relief proctor</span>
            <select name="to" className="select" disabled={handoverLoading || handoverSaving}>
              {targetTeam.map(item => (
                <option key={item.assignment_id} value={item.assignment_id}>
                  {item.proctor_name} · {item.assignment_role}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Current room state</span>
            <select name="state" className="select">
              <option value="live_stable">Live · stable</option>
              <option value="technical_monitoring">Live · technical monitoring</option>
              <option value="paused_under_policy">Paused under policy</option>
              <option value="closing_submission">Closing and submission</option>
            </select>
          </label>
          <label className="field full">
            <span>Open candidate support</span>
            <textarea
              name="support"
              className="textarea"
              defaultValue="Two approved breaks; one candidate reconnecting; accessibility alternative panel active."
            />
          </label>
          <label className="field full">
            <span>Open incidents</span>
            <textarea
              name="incidents"
              className="textarea"
              defaultValue="INC-2026-0041 and INC-2026-0044 need reviewer follow-up. No outcome finding recorded."
            />
          </label>
          <label className="field full">
            <span>Required next action</span>
            <textarea
              name="next"
              className="textarea"
              defaultValue="Continue room monitoring, confirm reconnect, and preserve the 15:00 checkpoint."
            />
          </label>
        </div>
        <div className="form-note">
          <Icon name="history" size={16} />
          Both exact assignments are rechecked on creation and target acceptance. The handover transfers
          factual context only; it does not change assignment or academic authority.
        </div>
        {handoverError && <div className="form-error">{handoverError}</div>}
        {!handoverLoading && handoverSessions.length === 0 && (
          <EmptyState
            icon="calendar"
            title="No current assigned session"
            text="A canonical current assignment is required."
          />
        )}
        {!handoverLoading && selectedSession && targetTeam.length === 0 && (
          <EmptyState
            icon="users"
            title="No eligible relief Proctor"
            text="Another current canonical assignment is required."
          />
        )}
        {handoverRecords.length > 0 && (
          <div className="stack" style={{ marginTop: 14 }}>
            {handoverRecords.map(record => (
              <div className="form-note" key={record.id}>
                <Icon name={record.state === "accepted" ? "check" : "history"} size={16} />
                <div style={{ flex: 1 }}>
                  <b>
                    {record.from_proctor_name} → {record.to_proctor_name} · {record.state}
                  </b>
                  <p>{record.context.requiredNextAction}</p>
                </div>
                {record.state === "pending" && record.can_accept && (
                  <Button
                    small
                    variant="primary"
                    disabled={handoverSaving}
                    onClick={() => {
                      setHandoverSaving(true);
                      setHandoverError("");
                      void acceptProctorHandover(record.exam_session_id, record.id, record.version)
                        .then(() => loadHandoverWorkspace(record.exam_session_id))
                        .then(workspace => {
                          setHandoverTeam(workspace.team);
                          setHandoverRecords(workspace.handovers);
                          cb.toast(
                            "Handover accepted",
                            "Target acceptance is now attributable and durable.",
                            "good",
                          );
                        })
                        .catch(cause => {
                          const message = cause instanceof Error ? cause.message : "Refresh and try again.";
                          setHandoverError(message);
                          cb.toast("Handover not accepted", message, "warn");
                        })
                        .finally(() => setHandoverSaving(false));
                    }}
                  >
                    Accept
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button
          type="submit"
          form="handover-form"
          className="btn btn-primary"
          disabled={handoverLoading || handoverSaving || !selectedSession || targetTeam.length === 0}
        >
          <Icon name="check" size={16} />
          <span>{handoverSaving ? "Recording…" : "Create handover"}</span>
        </button>
      </>
    );
  } else if (modal.kind === "emergency") {
    body = (
      <form id="emergency-form" onSubmit={e => e.preventDefault()}>
        <div className="integrity-banner">
          <span>
            <Icon name="alert" />
          </span>
          <div>
            <b>Protect people first.</b>
            <p>
              Use the minimum necessary monitoring and evidence. Hand over protected matters to the correct
              specialist authority.
            </p>
          </div>
          <Status>Immediate</Status>
        </div>
        <div className="form-grid">
          <label className="field">
            <span>Emergency type</span>
            <select name="type" className="select">
              <option>Candidate safety or wellbeing</option>
              <option>Accessibility barrier</option>
              <option>Platform-wide outage</option>
              <option>Power or network disruption</option>
              <option>Evacuation / environmental safety</option>
              <option>Safeguarding concern</option>
              <option>Evidence service unavailable</option>
            </select>
          </label>
          <label className="field">
            <span>Authorized immediate action</span>
            <select name="action" className="select">
              <option>Pause affected candidate timer</option>
              <option>Pause room timer</option>
              <option>Stop non-essential monitoring</option>
              <option>Open approved alternative delivery</option>
              <option>Call relief proctor</option>
              <option>Start evacuation playbook</option>
            </select>
          </label>
          <label className="field full">
            <span>Observable situation</span>
            <textarea
              name="facts"
              className="textarea"
              required
              placeholder="Describe what is happening now without diagnosis or outcome speculation."
            />
          </label>
          <label className="field full">
            <span>Contact / handover</span>
            <select name="contact" className="select">
              <option>Safeguarding team</option>
              <option>Accessibility Office</option>
              <option>Technical incident lead</option>
              <option>Assessment Operations</option>
              <option>Emergency services through institutional procedure</option>
            </select>
          </label>
        </div>
        <div className="form-note">
          <Icon name="alert" size={16} />
          This screen cannot activate an emergency control or record its effect. Follow the current human
          emergency procedure.
        </div>
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button type="submit" form="emergency-form" className="btn btn-danger" disabled>
          <Icon name="alert" size={16} />
          <span>Record and activate action</span>
        </button>
      </>
    );
  } else if (modal.kind === "language") {
    body = (
      <div>
        <div className="form-grid">
          <label className="field">
            <span>Primary language</span>
            <select
              className="select"
              value={prefs.primaryLanguage}
              onChange={e => setPrefs(v => ({ ...v, primaryLanguage: e.target.value }))}
            >
              {LANGUAGE_NAMES.map(language => (
                <option key={language}>{language}</option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Secondary translation</span>
            <select
              className="select"
              value={prefs.secondaryLanguage}
              onChange={e => setPrefs(v => ({ ...v, secondaryLanguage: e.target.value }))}
            >
              {LANGUAGE_NAMES.map(language => (
                <option key={language}>{language}</option>
              ))}
              <option>None</option>
            </select>
          </label>
          <div className="detail-item full">
            <small>Translation rule</small>
            <b>
              Preserve the original message, label the translated view, and retain the candidate’s own words
              for any formal statement.
            </b>
          </div>
        </div>
        <div className="switch-list" style={{ marginTop: 10 }}>
          <div className="switch-row">
            <span>
              <b>Show translated views</b>
              <small>Display the approved secondary translation beside the original.</small>
            </span>
            <Toggle
              value={prefs.translations}
              onChange={() => setPrefs(v => ({ ...v, translations: !v.translations }))}
              label="Show translated views"
            />
          </div>
          <div className="switch-row">
            <span>
              <b>Carry captions into Chatoner Class</b>
              <small>Include the caption preference in authorized Class handoffs.</small>
            </span>
            <Toggle
              value={prefs.captions}
              onChange={() => setPrefs(v => ({ ...v, captions: !v.captions }))}
              label="Carry captions"
            />
          </div>
        </div>
      </div>
    );
    footer = (
      <>
        <Button onClick={close}>Close</Button>
        <Button
          variant="primary"
          icon="check"
          onClick={() => {
            cb.toast(
              "Language view updated",
              `${prefs.primaryLanguage} + ${prefs.secondaryLanguage} is active; originals remain available.`,
              "good",
            );
            close();
          }}
        >
          Apply
        </Button>
      </>
    );
  } else if (modal.kind === "notifications") {
    const notes = payload?.notifications || [];
    body = selectedNotification ? (
      <div className="notification-detail-view">
        <button className="notification-back" onClick={() => setSelectedNotification(null)}>
          <Icon name="arrow" size={14} /> Back to notifications
        </button>
        <span className="notification-detail-icon">
          <Icon name="bell" />
        </span>
        <span className="notification-detail-label">Read · {selectedNotification.time}</span>
        <h3>{selectedNotification.title}</h3>
        <p>{selectedNotification.message}</p>
      </div>
    ) : (
      <div className="notification-list">
        {notes.map((n: any) => (
          <button
            className={`notification-item ${n.read ? "" : "unread"}`}
            key={n.id}
            onClick={() => {
              if (!n.read)
                void payload
                  ?.onRead?.(String(n.id))
                  .catch(() => cb.toast("Notifications unavailable", "Try again later.", "warn"));
              setSelectedNotification(n);
            }}
          >
            <span className="notification-copy">
              <b>{n.title}</b>
              <span>{n.message}</span>
              <small>{n.time}</small>
            </span>
            <Icon name="arrow" size={14} />
          </button>
        ))}
      </div>
    );
    footer = (
      <>
        {selectedNotification ? (
          <Button
            variant="primary"
            onClick={() => {
              close();
              if (selectedNotification.deepLink) payload?.openLink?.(selectedNotification.deepLink);
              else cb.navigate(selectedNotification.area, selectedNotification.page);
            }}
          >
            Open related page
          </Button>
        ) : (
          <Button
            icon="check"
            onClick={() => {
              void payload
                ?.onReadAll?.()
                .then(() => cb.toast("Notifications read", "Your notification history was updated.", "good"))
                .catch(() => cb.toast("Notifications unavailable", "Try again later.", "warn"));
            }}
          >
            Mark all read
          </Button>
        )}
        {selectedNotification ? <Button onClick={() => setSelectedNotification(null)}>Back</Button> : null}
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "profile") {
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item full">
            <small>Exam Proctor</small>
            <b>{identity.name}</b>
          </div>
          <div className="detail-item">
            <small>Email verification</small>
            <b>{account.security?.emailVerified ? "Email verified" : "Verification not available"}</b>
          </div>
          <div className="detail-item">
            <small>Profile headline</small>
            <b>{account.profile?.headline || "Not recorded"}</b>
          </div>
          <div className="detail-item">
            <small>Country</small>
            <b>{account.profile?.country || "Not recorded"}</b>
          </div>
          <div className="detail-item">
            <small>Time zone</small>
            <b>{account.profile?.timezone || "Not recorded"}</b>
          </div>
          <div className="detail-item">
            <small>Account sessions</small>
            <b>
              {account.security ? `${account.security.sessions.length} active sessions` : "Loading account"}
            </b>
          </div>
          <div className="detail-item">
            <small>Decision authority</small>
            <b>{identity.role || "No role assigned"}</b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          This role does not grade, decide misconduct, issue sanctions or access unrelated learner
          information.
        </div>
      </>
    );
    footer = (
      <>
        <Button
          icon="gear"
          onClick={() => {
            close();
            cb.navigate("governance", "security-sessions");
          }}
        >
          Settings
        </Button>
        <Button icon="logout" onClick={signOut}>
          Sign out
        </Button>
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "accommodation-detail" && accommodation) {
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item">
            <small>Candidate</small>
            <b>{accommodation.candidate}</b>
          </div>
          <div className="detail-item">
            <small>Status</small>
            <b>
              <Status>{accommodation.status}</Status>
            </b>
          </div>
          <div className="detail-item">
            <small>Approved adjustment</small>
            <b>{accommodation.type}</b>
          </div>
          <div className="detail-item">
            <small>Authorizing office</small>
            <b>{accommodation.owner}</b>
          </div>
          <div className="detail-item full">
            <small>Session</small>
            <b>{accommodation.session}</b>
          </div>
          <div className="detail-item full">
            <small>Minimum proctor instruction</small>
            <b>{accommodation.instructions}</b>
          </div>
        </div>
        <div className="form-note">
          <Icon name="accessibility" size={16} />
          No diagnosis or protected details are exposed. Contact the authorizing office rather than changing
          the plan.
        </div>
      </>
    );
    footer = (
      <>
        <Button
          icon="message"
          onClick={() => cb.openModal("candidate-message", { name: accommodation.owner })}
        >
          Contact office
        </Button>
        <Button icon="history" variant="primary" onClick={close}>
          {accommodation.applicationState === "applied"
            ? "Applied evidence recorded"
            : "Return to application"}
        </Button>
        <Button onClick={close}>Close</Button>
      </>
    );
  } else if (modal.kind === "break-action") {
    const name =
      (payload as Candidate)?.name || (payload as Accommodation)?.candidate || "Selected candidate";
    body = (
      <form id="break-form" onSubmit={e => e.preventDefault()}>
        <div className="detail-item">
          <small>Candidate</small>
          <b>{name}</b>
        </div>
        <div className="form-grid" style={{ marginTop: 10 }}>
          <label className="field">
            <span>Approved action</span>
            <select name="action" className="select">
              <option>Approved break</option>
              <option>Technical pause</option>
              <option>Accessibility pause</option>
              <option>Reconnect and re-entry</option>
              <option>Apply approved extra time</option>
              <option>Resume after approved break</option>
            </select>
          </label>
          <label className="field">
            <span>Authority source</span>
            <select name="authority" className="select">
              <option>Existing accommodation plan</option>
              <option>Assessment Operations authorization</option>
              <option>Technical continuity policy</option>
              <option>Emergency procedure</option>
            </select>
          </label>
          <label className="field full">
            <span>Factual note</span>
            <textarea
              name="note"
              className="textarea"
              defaultValue="Action applied under the existing approved plan; candidate work and timer state preserved."
            />
          </label>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          Break, re-entry and timer changes are unavailable until the server verifies the exact sitting and
          approved policy. No time or candidate state changes.
        </div>
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button type="submit" form="break-form" className="btn btn-primary" disabled>
          <Icon name="check" size={16} />
          <span>Apply approved action</span>
        </button>
      </>
    );
  } else if (modal.kind === "policy-confirmation") {
    body = (
      <>
        <div className="detail-grid">
          <div className="detail-item full">
            <small>Policy</small>
            <b>{payload?.title || "Exam Proctor authority and privacy policy"}</b>
          </div>
          <div className="detail-item full">
            <small>Summary</small>
            <b>
              {payload?.text ||
                "Monitor assigned sessions, support candidates neutrally, apply approved adjustments, record observable facts, preserve purpose-limited evidence and escalate to the correct human authority."}
            </b>
          </div>
        </div>
        <div className="boundary-grid" style={{ marginTop: 10 }}>
          <div className="boundary-card allow">
            <h3>
              <Icon name="check" />I understand
            </h3>
            <ul>
              {[
                "Automated signals are not findings.",
                "Candidate explanations must remain in their own words.",
                "Class and Labs independently enforce their product boundaries.",
                "Every consequential action remains human and attributable.",
              ].map(v => (
                <li key={v}>
                  <Icon name="check" size={12} />
                  {v}
                </li>
              ))}
            </ul>
          </div>
          <div className="boundary-card deny">
            <h3>
              <Icon name="lock" />I will not
            </h3>
            <ul>
              {[
                "Grade, coach, accuse or sanction.",
                "Use facial recognition as an autonomous decision.",
                "Broaden surveillance beyond published policy.",
                "Export or retain unrelated data.",
              ].map(v => (
                <li key={v}>
                  <Icon name="close" size={12} />
                  {v}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          Policy acknowledgement is unavailable until the effective document and attributable server receipt
          are connected. No acknowledgement is recorded.
        </div>
      </>
    );
    footer = (
      <>
        <Button onClick={close}>Close</Button>
        <Button icon="check" variant="primary" disabled>
          Acknowledge
        </Button>
      </>
    );
  } else if (modal.kind === "secure-note") {
    const governedAdmissionLock = payload?.control === "lock_new_admissions";
    const governedTimerControl = payload?.control === "pause_timer" || payload?.control === "resume_timer";
    const governedRoomControl = governedAdmissionLock || governedTimerControl;
    body = (
      <form
        id="note-form"
        onSubmit={async e => {
          e.preventDefault();
          if (!governedRoomControl || !payload?.examSessionId || roomControlSaving) return;
          const reason = String(new FormData(e.currentTarget).get("note") || "").trim();
          if (reason.length < 10) {
            setRoomControlError("Record at least 10 characters of factual reason.");
            return;
          }
          setRoomControlSaving(true);
          setRoomControlError("");
          try {
            if (governedAdmissionLock) {
              await lockProctorSessionAdmissions(payload.examSessionId, reason);
              cb.toast(
                "Admissions locked",
                "Future candidate admissions are now closed for this session.",
                "good",
              );
            } else {
              await applyProctorSessionTimerControl(
                payload.examSessionId,
                payload.control,
                reason,
                payload.pauseId,
              );
              cb.toast(
                payload.control === "pause_timer" ? "Room timer paused" : "Room timer resumed",
                "The canonical server clock recorded the attributable control.",
                "good",
              );
            }
            close();
          } catch (cause) {
            setRoomControlError(
              cause instanceof Error ? cause.message : "The room control could not be recorded.",
            );
          } finally {
            setRoomControlSaving(false);
          }
        }}
      >
        <div className="form-grid">
          <label className="field full">
            <span>Note title</span>
            <input
              name="title"
              className="input"
              defaultValue={payload?.title || "Proctor operational note"}
            />
          </label>
          <label className="field">
            <span>Purpose</span>
            <select name="purpose" className="select">
              <option>Session operation</option>
              <option>Technical continuity</option>
              <option>Candidate support</option>
              <option>Handover</option>
              <option>Policy exception request</option>
            </select>
          </label>
          <label className="field">
            <span>Source</span>
            <select name="source" className="select">
              <option>Human observation</option>
              <option>Candidate message</option>
              <option>System event</option>
              <option>Authorized office</option>
            </select>
          </label>
          <label className="field full">
            <span>Factual note</span>
            <textarea
              name="note"
              className="textarea"
              required
              minLength={governedRoomControl ? 10 : undefined}
              defaultValue={governedRoomControl ? "" : payload?.text || ""}
              placeholder="Record observable facts, source, action and next step…"
            />
          </label>
        </div>
        <div className="form-note">
          <Icon name="lock" size={16} />
          {governedAdmissionLock
            ? "This one-way control closes future admitted decisions. Existing admissions, entries and academic records are unchanged."
            : governedTimerControl
              ? "This records one global-room timer event. Effective pause time is server-derived and policy-capped; no extension or academic result is created."
              : "Operational notes are unavailable until an attributed server record is connected. No note is saved."}
        </div>
        {roomControlError ? (
          <p className="form-error" role="alert">
            {roomControlError}
          </p>
        ) : null}
      </form>
    );
    footer = (
      <>
        <Button onClick={close}>Cancel</Button>
        <button
          type="submit"
          form="note-form"
          className="btn btn-primary"
          disabled={
            !governedRoomControl ||
            !payload?.examSessionId ||
            (governedAdmissionLock && payload?.admissionsLocked) ||
            (payload?.control === "resume_timer" && !payload?.pauseId) ||
            roomControlSaving
          }
        >
          <Icon name="check" size={16} />
          <span>
            {roomControlSaving
              ? "Recording…"
              : governedAdmissionLock
                ? "Lock admissions"
                : payload?.control === "pause_timer"
                  ? "Pause timer"
                  : payload?.control === "resume_timer"
                    ? "Resume timer"
                    : "Save note"}
          </span>
        </button>
      </>
    );
  } else {
    body = (
      <EmptyState
        icon="help"
        title={payload?.title || titleMap[modal.kind] || "Proctor help"}
        text="Open the relevant session, candidate, policy or escalation contact. Use the documented procedure and keep any action factual, proportionate and attributable."
        action={
          <Button
            icon="message"
            onClick={() => cb.openModal("candidate-message", { name: "Assessment Operations" })}
          >
            Contact support
          </Button>
        }
      />
    );
  }

  return (
    <div
      className={`modal-backdrop ${modal.kind === "notifications" ? "notification-drawer-backdrop" : ""}`}
      role="presentation"
      onMouseDown={e => {
        if (e.currentTarget === e.target) close();
      }}
    >
      <section
        className={`modal ${wide ? "wide" : ""} ${modal.kind === "notifications" ? "notification-drawer" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={titleMap[modal.kind || ""] || "Dialog"}
      >
        <header className="modal-head">
          <span>
            <Icon
              name={
                modal.kind?.includes("incident")
                  ? "alert"
                  : modal.kind?.includes("class")
                    ? "video"
                    : modal.kind?.includes("lab")
                      ? "flask"
                      : modal.kind?.includes("evidence")
                        ? "database"
                        : modal.kind?.includes("language")
                          ? "globe"
                          : "shield"
              }
            />
          </span>
          <span className="modal-head-copy">
            <b>{titleMap[modal.kind || ""] || "Exam Proctor"}</b>
            <small>{subtitleMap[modal.kind || ""] || "Human-led assessment operations"}</small>
          </span>
          <button type="button" className="modal-close" onClick={close} aria-label="Close dialog">
            <Icon name="close" />
          </button>
        </header>
        <div className="modal-body">{body}</div>
        <footer className="modal-foot">{footer}</footer>
      </section>
    </div>
  );
}
